import { Plugin, ScreensPlugin, StagePlugin } from '@goodboydigital/astro';
import SoundBoy from '@goodboydigital/soundboy';
import { Ticker } from 'pixi.js';

import { App, Shared } from '../App';
import { Settings } from '../AppConfig';
import { NoTransition } from '../screens/transitions/NoTransition';
import { Browser } from '../utils/Browser';

// this thing is in charge of overall state of the game
// this includes, volume, pause, start/stop game
export class ControllerPlugin extends Plugin
{
    public screens!: ScreensPlugin;
    public appSuspended = false;
    public shortcutMap!: Record<string, () => void>;
    
    private wasMuted: boolean = false;

    init(): void
    {
        
        this.screens = this.app.get(ScreensPlugin as any);
        this.shortcutMap = {
            title: () => this.toHome(),
            preRace: () => this.toPreRace(),
            results: () => this.toResults(),
            garage: () => this.toGarage(),
            rideStudio: () => this.toRideStudio(),
            bodyStore: () => this.toBodyStore(),
            wheelStore: () => this.toWheelStore(),
            trailStore: () => this.toTrailStore(),
            extrasStore: () => this.toExtrasStore(),
            characterSelect: () => this.toCharacterSelect(),
            languageSelect: () => this.toLanguageSelect(),
            changeName: () => this.toChangeName(),
            flagSelect: () => this.toFlagSelect(),
            settings: () => this.toSettings(),
            rank: () => this.toRank(),
            tutorialComplete: () => this.toTutorialComplete(),
        };
    }

    async start(): Promise<void>
    {
        
        this.screens.overlay.transitionManager.register(new NoTransition(), 'empty', 'splash');
        this.screens.overlay.transitionManager.register(new NoTransition(), 'empty', 'changeName');
        this.screens.overlay.transitionManager.register(new NoTransition(), 'empty', 'languageSelect');

        if (Settings.browserLite || Settings.autoAccept || Settings.fastplay)
        {
            Shared.settings.termsAccepted = true;
            
            if (!Shared.settings.language) Shared.settings.language = 'en_GB';
        }
        
        // Shortcut to go straight to gameplay, for testing only
        if (Settings.fastplay || Settings.spineDebug)
        {
            setTimeout(() => this.toGameplay(), 300);

            return;
        }
        
        // Regular app start up
        if (!Shared.settings.language)
        {
            // If no best language has been found, go to language select
            this.toLanguageSelect(true);
        }
        else if (!Shared.settings.termsAccepted)
        {
            Shared.settings.termsAccepted = true;
            if(Settings.isApp()){
                // Start with GDPR, if language is set but terms not accepted
                this.toTerms(true);
            }
            else{
                //Shared.settings.termsAccepted = true;
                await this.startGame();
                this.toSplash(true);
            }
            
        }
        else
        {
            // Start the app straight to a screen, defaults to splash
            const starting = Settings.to;
            const shortcut = this.shortcutMap[starting];

            shortcut ? shortcut() : this.toSplash(true);
        }
    }

    async startGame(): Promise<void>
    {
        if (Shared.game) return;
        this.screens.overlay.goto('empty');
        await this.screens.goto('game');
    }
    
    async toSplash(firstTime = false): Promise<void>
    {
        await this.startGame();
        
        this.screens.overlay.goto('splash', { data: { firstTime } });
        
    }

    async toHome(): Promise<void>
    {
        
        await this.startGame();
        await this.screens.overlay.goto('home');
        Shared.game.hud.showCoinsAndDiamonds();
    }

    async toGameplay(race?: number): Promise<void>
    {
        await this.startGame();
        if (Settings.spineDebug) return;
        if (race === undefined || race < 0) race = Shared.user.data.race;
        Shared.user.setRace(race);
        console.log('RACE', Shared.user.data.race);
        this.screens.overlay.goto('controls');
        Shared.game.play();
    }

    async toPreRace(): Promise<void>
    {
        await this.startGame();
        // Prevent full screen while testing races
        if (Settings.race < 0) Browser.enterFullScreen();
        Shared.game.idle();
        
        Shared.game.hud.showCoinsAndDiamonds();
        
        if (Shared.user.data.race > 0)
        {
            await this.screens.overlay.goto('preRace');
        }
        else
        {
            this.toGameplay(0);
        }

    }

    async toGarage(): Promise<void>
    {
        await this.startGame();
        Shared.game.idle();
        Browser.enterFullScreen();
        await this.screens.overlay.goto('garage');
    }

    async toRideStudio(): Promise<void>
    {
        await this.startGame();
        await this.screens.overlay.goto('rideStudio');
    }

    async toBodyStore(): Promise<void>
    {
        await this.startGame();
        await this.screens.overlay.goto('bodyStore');
    }

    async toWheelStore(): Promise<void>
    {
        await this.startGame();
        await this.screens.overlay.goto('wheelStore');
    }

    async toTrailStore(): Promise<void>
    {
        await this.startGame();
        await this.screens.overlay.goto('trailStore');
    }

    async toExtrasStore(): Promise<void>
    {
        await this.startGame();
        await this.screens.overlay.goto('extrasStore');
    }

    async toCharacterSelect(): Promise<void>
    {
        await this.startGame();
        await this.screens.overlay.goto('characterSelect');
    }

    async toResults(): Promise<void>
    {
        await this.startGame();
        if (Shared.game.race.raceIndex === 0 && Settings.to !== 'results')
        {
            await this.toTutorialComplete();
        }
        else
        {
            await this.screens.overlay.goto('results');
        }
    }

    async toRank(): Promise<void>
    {
        await this.startGame();
        await this.screens.overlay.goto('rank');
    }

    async toTutorialComplete(): Promise<void>
    {
        await this.startGame();
        await this.screens.overlay.goto('tutorialComplete');
    }

    async toSettings(backToSplash?: boolean): Promise<void>
    {
        await this.startGame();
        Browser.enterFullScreen();
        await this.screens.overlay.goto('settings', { data: { backToSplash } });
    }

    async toTerms(firstTime = false): Promise<void>
    {
        this.screens.overlay.transitionManager.register(new NoTransition(), 'empty', 'terms')
        await this.startGame();
        await this.screens.overlay.goto('terms', { data: { firstTime } });
    }

    async toChangeName(firstTime = false, backToHome = false, proceedToHome = false): Promise<void>
    {
        if (!firstTime) await this.startGame();
        await this.screens.overlay.goto('changeName', { data: { firstTime, backToHome, proceedToHome } });
    }

    async toFlagSelect(firstTime = false, backToHome = false, proceedToHome = false): Promise<void>
    {
        if (!firstTime) await this.startGame();
        await this.screens.overlay.goto('flagSelect', { data: { firstTime, backToHome, proceedToHome } });
    }

    async toLanguageSelect(firstTime = false): Promise<void>
    {
        await this.startGame();
        await this.screens.overlay.goto('languageSelect', { data: { firstTime } });
    }

    async playNextRace(): Promise<void>
    {
        this.toPreRace();
    }

    async playPrevRace(): Promise<void>
    {
        Shared.user.prevRace();
        this.toPreRace();
    }

    public hideAndSuspendApp(): void
    {
        if (this.appSuspended) return;
        this.appSuspended = true;

        const stage = this.app.get(StagePlugin as any) as StagePlugin;

        document.body.removeChild(stage.canvas);

        Ticker.system.remove(this.app.update, this.app);
        
        this.wasMuted = SoundBoy.muted;
        
        if(!this.wasMuted){
            SoundBoy.muted = true;
        }
        
    }

    public showAndResumeApp(): void
    {
        SoundBoy.muted = Shared.game.app.user.data.settings.mute;
        
        if (!this.appSuspended) {
            return;
        }
        this.appSuspended = false;

        const stage = this.app.get(StagePlugin as any) as StagePlugin;

        document.body.appendChild(stage.canvas);

        Ticker.system.add(this.app.update, this.app);
        
    }
}
